import React, { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import AppDialog from '@/shared/components/ui/AppDialog'
import { fetchServicesApi, type Service } from '@/features/system/services/api/servicesApi'
import { useTenants } from '../hooks/useTenants'

interface Props {
  open: boolean
  onOpenChange: (v: boolean) => void
}

export default function CreateTenantDialog({ open, onOpenChange }: Props) {
  const { create } = useTenants()
  const [name, setName] = useState('')
  const [sel,  setSel ] = useState<Set<number>>(new Set())

  const { data: services = [], isLoading: svcLoading, isError: svcErr } =
    useQuery<Service[], Error>({
      queryKey : ['services'],
      queryFn  : fetchServicesApi,
      staleTime: 300_000,
    })

  const toggle = (id: number) =>
    setSel(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })

  const canCreate = name.trim().length > 0 && !create.isPending

  const handleCreate = async () => {
    if (!canCreate) return
    await create.mutateAsync({
      tenant_name: name.trim(),
      service_ids: Array.from(sel),
    })
    setName('')
    setSel(new Set())
    onOpenChange(false)
  }

  const svcSorted = useMemo(
    () => [...services].sort((a,b)=>a.service_name.localeCompare(b.service_name)),
    [services],
  )

  return (
    <AppDialog
      open={open}
      onOpenChange={(v) => {
        // if closing, reset local state
        if (!v) { setName(''); setSel(new Set()) }
        onOpenChange(v)
      }}
      title="New tenant"
      description="Create a tenant and select which backend services belong to it."
      size="md"
      onConfirm={handleCreate}
      confirmLabel={create.isPending ? 'Creating…' : 'Create tenant'}
      confirmDisabled={!canCreate}
      isBusy={create.isPending}
    >
      <label className="block text-sm text-neutral-300">
        Tenant name
        <input
          autoFocus
          value={name}
          onChange={e=>setName(e.target.value)}
          placeholder="e.g. acme-inc"
          className="mt-1 w-full bg-neutral-800 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </label>

      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-neutral-300">Services</span>
          {!!sel.size && (
            <button
              onClick={() => setSel(new Set())}
              className="text-xs text-neutral-400 hover:text-neutral-200"
            >
              Clear ({sel.size})
            </button>
          )}
        </div>

        <div className="max-h-56 overflow-auto border border-neutral-800 rounded">
          {svcLoading && <p className="p-3 text-neutral-400">Loading services…</p>}
          {svcErr && <p className="p-3 text-rose-400">Failed to load services.</p>}
          {!svcLoading && !svcErr && (
            <ul className="divide-y divide-neutral-800">
              {svcSorted.map(s => (
                <li key={s.id_service} className="flex items-center gap-2 px-3 py-2">
                  <input
                    id={`svc-${s.id_service}`}
                    type="checkbox"
                    checked={sel.has(s.id_service)}
                    onChange={() => toggle(s.id_service)}
                    className="h-4 w-4 accent-emerald-600"
                  />
                  <label htmlFor={`svc-${s.id_service}`} className="cursor-pointer">
                    <span className="text-neutral-100">{s.service_name}</span>
                    <span className="ml-2 text-xs text-neutral-500">#{s.id_service}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {create.isError && (
        <p className="text-sm text-rose-400">
          {(create.error as Error)?.message ?? 'Could not create tenant.'}
        </p>
      )}
    </AppDialog>
  )
}
